import { zodResolver } from "@hookform/resolvers/zod";
import { UpdateProductGroupInput, ProductGroup } from "generated/graphql";
import { useForm, Controller } from "react-hook-form";
import { Card, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField/FormField";
import MDButton from "components/MDButton";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import ProductOptions from "modules/products/ProductOptions";
import { z } from "zod";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  productIds: z.array(z.string()).min(1, "At least one product is required"),
});

interface ProductGroupUpdateFormProps {
  onSubmit: (values: UpdateProductGroupInput) => Promise<{ success: boolean }>;
  loading: boolean;
  productGroup: ProductGroup;
}

export default function ProductGroupUpdateForm({
  onSubmit,
  loading,
  productGroup,
}: ProductGroupUpdateFormProps) {
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<UpdateProductGroupInput & { productIds: string[] }>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: productGroup.name,
      productIds: productGroup.products.map((product) => product.id),
    },
  });

  return (
    <Card>
      <MDBox
        p={3}
        component="form"
        role="form"
        onSubmit={handleSubmit(async (values) => {
          const { productIds, ...restValues } = values;
          const input: UpdateProductGroupInput = {
            id: productGroup.id,
            ...restValues,
            products: {
              sync: productIds,
            },
          };
          const result = await onSubmit(input);
          if (result.success) {
            reset();
          }
        })}
      >
        <MDTypography variant="h5">Product Group Information</MDTypography>
        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormField
                type="text"
                label="Name"
                placeholder="Enter product group name"
                error={errors.name}
                {...register("name")}
              />
            </Grid>
            <Grid item xs={12}>
              <ProductOptions>
                {({ productOptions, loading: productsLoading, search }) => (
                  <Controller
                    name="productIds"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <AutoComplete
                        options={productOptions}
                        field={field}
                        loading={productsLoading}
                        error={error?.message ? error : ""}
                        label="Select Products"
                        search={search}
                        multiple={true}
                        disableCloseOnSelect={true}
                      />
                    )}
                  />
                )}
              </ProductOptions>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="space-between">
              <MDButton type="submit" variant="gradient" color="success" disabled={loading}>
                Update Product Group
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
